import { FileState } from '@atlaskit/media-state';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { colors } from '@atlaskit/theme';
import Tooltip from '@atlaskit/tooltip';
import { useFlaggingCallback } from '@databases/flags';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import { DatabaseFieldType, DatabaseValue } from '@databases/types/database';
import { MultiValueChangeType } from '@databases/utils/database/changesets';
import { getMediaCollectionId } from '@databases/utils/media-client';
import { generateId } from '@databases/utils/utils';
import classNames from 'classnames';
import React, { FunctionComponent, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { useMediaClient } from '../../../media-client';
import { InternalDatabaseValueView } from '../../database-value/DatabaseValueView';
import { DatabaseValueViewContainer } from '../../database-value/utils';
import { DropZone } from '../../styled';
import { EditComponentProps } from '../fieldTypes';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 100%;
  min-height: 36px;
  position: relative;
  height: 100%;

  > div {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }

  * {
    // Set the outline color to the same color as the border color
    outline-color: ${colors.B100};
    outline-offset: 1px;
  }

  &:hover {
    div {
      opacity: 1;
    }
  }
`;

const StyledDropZone = styled(DropZone).attrs({ tabIndex: 0 as number })<{
  isDragActive: boolean;
  isEmpty: boolean;
}>`
  flex-direction: row;
  flex-grow: 1;
  align-items: center;
  justify-content: ${(props) => (props.isEmpty ? 'center' : 'flex-start')};
  cursor: pointer;

  * {
    color: inherit;
  }

  ${DatabaseValueViewContainer} {
    width: 100%;
    height: 100%;
  }

  ${DatabaseValueViewContainer} > div:first-child {
    width: 100%;
    display: flex;
    justify-content: center;
  }

  ${Container}.is-seamless:not(:hover):not(:focus-within) > div > & {
    border-color: transparent;
  }
`;

const UploadingSpinnerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const AttachmentValueEdit: FunctionComponent<
  EditComponentProps<DatabaseFieldType.ATTACHMENT>
> = ({
  contentId,
  entryId,
  onChange,
  onFocus,
  onBlur,
  field,
  value,
  isSeamless = false,
}) => {
  const mediaClient = useMediaClient(contentId);
  const store = defaultStoreUtils.useStore();
  const [isUploading, setIsUploading] = useState(false);
  const { formatMessage } = useIntl();

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onFileDialogCancel: () => {
      onBlur?.();
    },
    multiple: false,
    noClick: value && !!value.length, // Click will be handled by actions when theres a value
    disabled: isUploading,
    onDrop: useFlaggingCallback(
      async (files) => {
        setIsUploading(true);

        try {
          const collectionId = await getMediaCollectionId(store.getState(), contentId);
          const fileState = await new Promise<
            Extract<FileState, { status: 'processed' | 'failed-processing' }>
          >((resolve, reject) => {
            mediaClient?.file
              .upload({
                content: files[0],
                name: files[0].name,
                size: files[0].size,
                mimeType: files[0].type,
                collection: collectionId,
              })
              .subscribe((v) => {
                if (v.status === 'processed' || v.status === 'failed-processing') {
                  resolve(v);
                } else if (v.status === 'error') {
                  console.error(v);
                  reject(new Error(`Error uploading media: ${v.message}`));
                }
              });
          });
          const attachmentValue = {
            _id: value[0]?._id ?? generateId(),
            fileId: fileState.id,
          };
          onChange([attachmentValue], {
            type: DatabaseFieldType.ATTACHMENT,
            delta:
              value.length > 0
                ? {
                    type: MultiValueChangeType.EDIT,
                    value: attachmentValue,
                    index: 0,
                  }
                : { type: MultiValueChangeType.APPEND, value: attachmentValue },
          });
        } finally {
          setIsUploading(false);
        }
      },
      [contentId, mediaClient?.file, onChange, store, value]
    ),
  });

  const { ref, ...rootProps } = getRootProps();

  const mappedValue = useMemo(
    (): DatabaseValue => ({ type: DatabaseFieldType.ATTACHMENT, value }),
    [value]
  );

  return (
    <Flex
      justifyContent="center"
      direction="column"
      xcss={xcss({ width: '100%', height: '100%' })}
    >
      <Box xcss={xcss({ width: '100%', display: 'inline-block' })}>
        <Tooltip
          content={
            (!value || !value.length) && !isUploading
              ? formatMessage(messages['table-layout.image-field-value-tooltip'])
              : ''
          }
        >
          <Container className={classNames({ 'is-seamless': isSeamless })}>
            <StyledDropZone
              {...rootProps}
              ref={ref}
              onFocus={onFocus}
              onBlur={onBlur}
              isDragActive={isDragActive}
              isEmpty={!value || !value.length}
            >
              {isUploading ? (
                <UploadingSpinnerContainer>
                  <Spinner />
                </UploadingSpinnerContainer>
              ) : (
                <InternalDatabaseValueView
                  contentId={contentId}
                  entryId={entryId}
                  field={field}
                  value={mappedValue}
                  showPlaceholder
                  handleChange={onChange}
                  onReplace={open}
                />
              )}
            </StyledDropZone>
            <input {...getInputProps()} />
          </Container>
        </Tooltip>
      </Box>
    </Flex>
  );
};

export default AttachmentValueEdit;
